import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  CreditCard,
  Hash,
  Mail,
  MapPin,
  Pencil,
  Percent,
  Phone,
  User as UsuarioIcono,
  UserPlus,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { Cliente, FormaDePagoSri, Sector } from '@/shared/api/tipos';
import { AccionesDelFormulario, Campo, Modal, claseEntrada } from '@/shared/ui/Modal';
import { Interruptor } from '@/shared/ui/Interruptor';
import { notificar } from '@/shared/ui/avisos';

type Pestana = 'datos' | 'ubicacion';

const esquema = z.object({
  identificacion: z
    .string()
    .trim()
    .min(10, 'La cédula tiene 10 dígitos y el RUC 13')
    .max(13, 'La cédula tiene 10 dígitos y el RUC 13')
    .regex(/^\d+$/, 'Solo dígitos'),
  nombre: z.string().trim().min(1, 'El nombre es obligatorio').max(255),
  telefono: z.string().max(20).optional(),
  email: z.string().max(255).optional(),
  direccion: z.string().max(500).optional(),
  sectorId: z.string().optional(),
  latitud: z.string().optional(),
  longitud: z.string().optional(),
  zutmX: z.string().optional(),
  zutmY: z.string().optional(),
  formaPagoSri: z.string().optional(),
  exentoInteresMora: z.boolean().optional(),
  activo: z.boolean().optional(),
});

type Formulario = z.infer<typeof esquema>;

export function DialogoDeCliente({
  abierto,
  cliente,
  sectores,
  formasDePago,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  cliente: Cliente | null;
  sectores: Sector[];
  formasDePago: FormaDePagoSri[];
  alCerrar: () => void;
  alGuardar: (cliente: Cliente) => void;
}) {
  const editando = cliente !== null;
  const [pestana, setPestana] = useState<Pestana>('datos');
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: {
      identificacion: cliente?.identificacion ?? '',
      nombre: cliente?.nombre ?? '',
      telefono: cliente?.telefono ?? '',
      email: cliente?.email ?? '',
      direccion: cliente?.direccion ?? '',
      sectorId: cliente?.sectorId == null ? '' : String(cliente.sectorId),
      latitud: cliente?.latitud == null ? '' : String(cliente.latitud),
      longitud: cliente?.longitud == null ? '' : String(cliente.longitud),
      zutmX: cliente?.zutmX == null ? '' : String(cliente.zutmX),
      zutmY: cliente?.zutmY == null ? '' : String(cliente.zutmY),
      formaPagoSri: cliente?.formaPagoSri ?? '',
      exentoInteresMora: cliente?.exentoInteresMora ?? false,
      activo: cliente?.activo ?? true,
    },
  });

  const enviar = handleSubmit(async (datos) => {
    setErrorGeneral(null);
    try {
      const cuerpo = {
        identificacion: datos.identificacion.trim(),
        nombre: datos.nombre.trim(),
        telefono: datos.telefono || null,
        email: datos.email || null,
        direccion: datos.direccion || null,
        sectorId: datos.sectorId ? Number(datos.sectorId) : null,
        latitud: datos.latitud ? Number(datos.latitud) : null,
        longitud: datos.longitud ? Number(datos.longitud) : null,
        zutmX: datos.zutmX ? Number(datos.zutmX) : null,
        zutmY: datos.zutmY ? Number(datos.zutmY) : null,
        formaPagoSri: datos.formaPagoSri || null,
        exentoInteresMora: datos.exentoInteresMora ?? false,
        activo: datos.activo ?? true,
      };

      const guardado = editando
        ? await api.put<Cliente>(`/clientes/${cliente.id}`, cuerpo)
        : await api.post<Cliente>('/clientes', cuerpo);

      alGuardar(guardado);
      alCerrar();
      notificar.exito(
        editando ? `Cliente actualizado: ${cuerpo.nombre}` : `Cliente creado: ${cuerpo.nombre}`,
      );
    } catch (causa) {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar el cliente';
      setErrorGeneral(mensaje);
      notificar.error(mensaje);
    }
  });

  const pestanas: { clave: Pestana; etiqueta: string; icono: LucideIcon }[] = [
    { clave: 'datos', etiqueta: 'Datos personales', icono: UsuarioIcono },
    { clave: 'ubicacion', etiqueta: 'Ubicación', icono: MapPin },
  ];

  return (
    <Modal
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar cliente' : 'Nuevo cliente'}
      subtitulo={
        editando ? `Editando: ${cliente.nombre}` : 'Ingrese los datos del nuevo contribuyente'
      }
      icono={editando ? Pencil : UserPlus}
      ancho="amplio"
      cerrarAlPulsarFuera={false}
    >
      <nav className="mb-5 flex gap-1 rounded-xl bg-armazon-50 p-1" aria-label="Secciones">
        {pestanas.map(({ clave, etiqueta, icono: Icono }) => (
          <button
            key={clave}
            type="button"
            onClick={() => setPestana(clave)}
            aria-current={pestana === clave}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold uppercase transition-colors ${
              pestana === clave
                ? 'bg-white text-marca-700 shadow-sm'
                : 'text-armazon-500 hover:text-armazon-700'
            }`}
          >
            <Icono className="size-3.5 shrink-0" aria-hidden />
            {etiqueta}
          </button>
        ))}
      </nav>

      <form onSubmit={enviar}>
        {errorGeneral && (
          <p
            role="alert"
            className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            {errorGeneral}
          </p>
        )}

        <div className={pestana === 'datos' ? 'space-y-3' : 'hidden'}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo etiqueta="Nombre completo" requerido error={errors.nombre?.message}>
              <ConIcono icono={UsuarioIcono}>
                <input
                  {...register('nombre')}
                  className={`${claseEntrada} pl-10`}
                  placeholder="Nombre y apellido"
                />
              </ConIcono>
            </Campo>

            <Campo
              etiqueta="Cédula / RUC"
              requerido
              error={errors.identificacion?.message}
              ayuda={
                editando
                  ? 'NO SE PUEDE CAMBIAR: LAS FACTURAS EMITIDAS LA LLEVAN IMPRESA'
                  : undefined
              }
            >
              <ConIcono icono={Hash}>
                <input
                  {...register('identificacion')}
                  className={`${claseEntrada} importe pl-10 ${editando ? 'bg-armazon-50' : ''}`}
                  placeholder="0501234567"
                  readOnly={editando}
                />
              </ConIcono>
            </Campo>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo etiqueta="Teléfono" error={errors.telefono?.message}>
              <ConIcono icono={Phone}>
                <input
                  {...register('telefono')}
                  className={`${claseEntrada} importe pl-10`}
                  placeholder="032000000"
                />
              </ConIcono>
            </Campo>

            <Campo etiqueta="Email" error={errors.email?.message}>
              <ConIcono icono={Mail}>
                <input
                  {...register('email')}
                  type="email"
                  className={`${claseEntrada} pl-10`}
                  placeholder="correo@email.com"
                />
              </ConIcono>
            </Campo>
          </div>

          <Campo etiqueta="Dirección" error={errors.direccion?.message}>
            <ConIcono icono={MapPin}>
              <input
                {...register('direccion')}
                className={`${claseEntrada} pl-10`}
                placeholder="Calle principal y secundaria"
              />
            </ConIcono>
          </Campo>

          <Seccion icono={CreditCard} titulo="Facturación electrónica">
            <Campo
              etiqueta="Forma de pago SRI"
              error={errors.formaPagoSri?.message}
              ayuda="CÓDIGO QUE VIAJA AL SRI EN EL COMPROBANTE. EN AUTOMÁTICO SE USA 20 PARA QUIEN TIENE RUC Y 01 PARA QUIEN TIENE CÉDULA."
            >
              <select {...register('formaPagoSri')} className={claseEntrada}>
                <option value="">DEFAULT AUTOMÁTICO (RUC → 20, CÉDULA → 01)</option>
                {formasDePago.map((f) => (
                  <option key={f.codigo} value={f.codigo}>
                    {f.nombre}
                  </option>
                ))}
              </select>
            </Campo>
          </Seccion>

          <div className="grid gap-x-6 sm:grid-cols-2">
            <div className={editando ? '' : 'sm:col-span-2'}>
              <Seccion icono={Percent} titulo="Interés por mora">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 shrink-0">
                    <Interruptor
                      activo={watch('exentoInteresMora') ?? false}
                      alCambiar={() =>
                        setValue('exentoInteresMora', !watch('exentoInteresMora'), {
                          shouldDirty: true,
                        })
                      }
                      etiqueta="Exento de interés por mora"
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium uppercase text-armazon-900">
                      Exento de interés por mora
                    </span>
                    <span className="mt-0.5 block hyphens-auto text-justify text-[11px] uppercase leading-relaxed text-armazon-500">
                      Para empresas públicas y similares. No se les cobra mora en ninguna pantalla:
                      caja, portal ciudadano, consulta pública y reportes. Aplica de inmediato a{' '}
                      <strong>toda</strong> su deuda pendiente, incluidas las facturas atrasadas. Lo
                      ya cobrado no se modifica.
                    </span>
                  </span>
                </div>
              </Seccion>
            </div>

            {editando && (
              <Seccion icono={UsuarioIcono} titulo="Estado del cliente">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 shrink-0">
                    <Interruptor
                      activo={watch('activo') ?? true}
                      alCambiar={() => setValue('activo', !watch('activo'), { shouldDirty: true })}
                      etiqueta="Cliente activo"
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium uppercase text-armazon-900">
                      Cliente activo
                    </span>
                    <span className="mt-0.5 block text-justify text-[11px] uppercase leading-relaxed text-armazon-500">
                      Desactivarlo lo saca de las listas sin borrar nada
                    </span>
                  </span>
                </div>
              </Seccion>
            )}
          </div>
        </div>

        <div className={pestana === 'ubicacion' ? 'space-y-4' : 'hidden'}>
          <Campo
            etiqueta="Sector"
            error={errors.sectorId?.message}
            ayuda="LA PARROQUIA DONDE VIVE. DE SU RURALIDAD SALE LA TARIFA CUANDO LA CUENTA NO LA TRAE."
          >

            <select {...register('sectorId')} className={claseEntrada}>
              <option value="">SELECCIONAR</option>
              {(['urbano', 'rural'] as const).map((ruralidad) => {
                const suyas = sectores.filter((s) => s.ruralidad === ruralidad);
                if (suyas.length === 0) {
                  return null;
                }
                return (
                  <optgroup
                    key={ruralidad}
                    label={ruralidad === 'urbano' ? 'PARROQUIAS URBANAS' : 'PARROQUIAS RURALES'}
                  >
                    {suyas.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.parroquia}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </Campo>

          <Seccion icono={MapPin} titulo="Coordenadas geográficas">
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                etiqueta="Latitud"
                error={errors.latitud?.message}
                ayuda="VAN JUNTAS O NINGUNA"
              >
                <input
                  {...register('latitud')}
                  className={`${claseEntrada} importe`}
                  placeholder="-1.0098"
                />
              </Campo>

              <Campo etiqueta="Longitud" error={errors.longitud?.message}>
                <input
                  {...register('longitud')}
                  className={`${claseEntrada} importe`}
                  placeholder="-78.6101"
                />
              </Campo>

              <Campo etiqueta="UTM X" error={errors.zutmX?.message}>
                <input
                  {...register('zutmX')}
                  className={`${claseEntrada} importe`}
                  placeholder="765973.559"
                />
              </Campo>

              <Campo etiqueta="UTM Y" error={errors.zutmY?.message}>
                <input
                  {...register('zutmY')}
                  className={`${claseEntrada} importe`}
                  placeholder="9896876.487"
                />
              </Campo>
            </div>
          </Seccion>
        </div>

        <AccionesDelFormulario
          alCancelar={alCerrar}
          enviando={isSubmitting}
          textoEnviar={editando ? 'Actualizar cliente' : 'Crear cliente'}
        />
      </form>
    </Modal>
  );
}

function ConIcono({ icono: Icono, children }: { icono: LucideIcon; children: ReactNode }) {
  return (
    <div className="relative">
      <Icono
        className="pointer-events-none absolute left-3.5 top-1/2 size-[15px] -translate-y-1/2 text-armazon-400"
        aria-hidden
      />
      {children}
    </div>
  );
}

function Seccion({
  icono: Icono,
  titulo,
  children,
}: {
  icono: LucideIcon;
  titulo: string;
  children: ReactNode;
}) {
  return (
    <div className="mt-2 border-t border-armazon-100 pt-2">
      <p className="mb-3 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-armazon-400">
        <Icono className="size-3 shrink-0" aria-hidden />
        {titulo}
      </p>
      {children}
    </div>
  );
}
